import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { App as AntApp } from 'antd';
import { useTranslation } from 'react-i18next';
// The heavy WalletConnect engine (@walletconnect/core, @reown/walletkit,
// @walletconnect/utils — ~0.5 MB gzipped) is dynamically imported on demand so it
// stays out of the initial bundle. Only jsonrpc-utils (tiny) is imported eagerly.
import type { IWalletKit } from '@reown/walletkit';
import { formatJsonRpcResult, formatJsonRpcError } from '@walletconnect/jsonrpc-utils';
import { vaultsApi } from '@/api/vaults';
import { chainKeyToChainId, chainKeyToCaip2, toCaip10Account } from '@/lib/walletconnect/chains';
import { classifyEvmCall, type EvmInnerCall } from '@/lib/evmCallSafety';
import {
  dappRecordLabel,
  resolveDappIdentity,
  type DappIdentity,
} from '@/lib/walletconnect/verify';
import {
  checkTypedDataChain,
  decodeTypedData,
  type DecodedTypedData,
  type TypedDataChainCheck,
} from '@/lib/walletconnect/typedData';
import {
  enqueueRequest,
  removeRequest,
  removeRequestId,
  removeTopic,
} from '@/lib/walletconnect/requestQueue';
import { adoptStoredBindings, findOrphanTopics } from '@/lib/walletconnect/bindings';
import type {
  EthereumTransaction,
  PendingDappTx,
  WcActiveSession,
  WcSessionProposal,
  WcSessionRequest,
  WcVaultBinding,
} from '@/types/walletconnect';

// Reown Cloud project id (shared across the SSP ecosystem — same as ssp-wallet).
const WALLETCONNECT_PROJECT_ID = '0fddbe43cb0cca6b6e0fcf9b5f4f0ff6';

// How long we hold a dApp's eth_sendTransaction open, waiting for the proposal to
// be approved + broadcast, before answering with a "queued for approval" error.
// Kept under the typical 5-minute WalletConnect request expiry.
const OPTIMISTIC_WINDOW_MS = 4 * 60 * 1000;
const PROPOSAL_POLL_INTERVAL_MS = 5000;

const BINDINGS_STORAGE_KEY = 'ssp_wc_bindings';

// Methods advertised to dApps. We advertise broadly so sessions build (many dApps
// list personal_sign in requiredNamespaces); unsupported methods are answered with
// a clear error at request time until Phase 2.
const SUPPORTED_METHODS = [
  'eth_accounts',
  'eth_requestAccounts',
  'eth_sendTransaction',
  'eth_sign',
  'eth_signTypedData',
  'eth_signTypedData_v3',
  'eth_signTypedData_v4',
  'personal_sign',
  'wallet_switchEthereumChain',
  'wallet_addEthereumChain',
  'eth_chainId',
  'net_version',
];
const SUPPORTED_EVENTS = ['chainChanged', 'accountsChanged'];

// Sign methods deferred to Phase 2 (dApp login / governance). Answered clearly.
const DEFERRED_SIGN_METHODS = new Set([
  'personal_sign',
  'eth_sign',
  'eth_signTypedData',
  'eth_signTypedData_v3',
  'eth_signTypedData_v4',
]);

export interface PendingTxRequest {
  request: WcSessionRequest;
  tx: EthereumTransaction;
  binding: WcVaultBinding;
  /** Who is really asking (Verify API), never the self-declared name alone. */
  identity: DappIdentity;
  /** The call as classified by the EVM call-safety rules. */
  call: EvmInnerCall;
}

export interface PendingSignRequest {
  request: WcSessionRequest;
  message: string;
  messageType: 'personal_sign' | 'eth_signTypedData_v4';
  binding: WcVaultBinding;
  identity: DappIdentity;
  /** Decoded typed data (eth_signTypedData*). */
  typedData?: DecodedTypedData;
  typedDataChain?: TypedDataChainCheck;
}

/**
 * A dApp personal_sign that became an M-of-N signature request and is awaiting
 * the signing threshold. When the request is 'signed' with a finalSignature we
 * return that to the dApp (else, past respondBy, answer "queued for approval").
 */
interface PendingDappSig {
  topic: string;
  requestId: number;
  signatureRequestId: string;
  orgId: string;
  vaultId: string;
  respondBy: number;
}

export interface WalletConnectContextValue {
  isInitialized: boolean;
  activeSessions: WcActiveSession[];
  bindings: Record<string, WcVaultBinding>;
  /** Pair with a dApp using a wc: URI, binding the future session to `binding`. */
  connect: (uri: string, binding: WcVaultBinding) => Promise<void>;
  disconnect: (topic: string) => Promise<void>;
  connecting: boolean;
  // Session proposal (connect) modal state
  pendingProposal: WcSessionProposal | null;
  pendingProposalBinding: WcVaultBinding | null;
  approveProposal: () => Promise<void>;
  rejectProposal: () => Promise<void>;
  // dApp transaction request modal state — the head of a FIFO queue; new
  // requests are appended, never swapped in under the user's cursor.
  pendingTxRequest: PendingTxRequest | null;
  txQueueSize: number;
  confirmTxRequest: () => Promise<void>;
  rejectTxRequest: () => Promise<void>;
  txRequestSubmitting: boolean;
  // dApp message signature request modal state (personal_sign / typed data)
  pendingSignRequest: PendingSignRequest | null;
  signQueueSize: number;
  /** Plain-text dApp label for records (memo / dappOrigin) — origin-based. */
  recordLabel: (identity: DappIdentity) => string;
  rejectSignRequest: () => Promise<void>;
  clearSignRequest: () => void;
  /** Hold the dApp's request open until the signature request completes. */
  registerPendingSignature: (entry: {
    topic: string;
    requestId: number;
    signatureRequestId: string;
    orgId: string;
    vaultId: string;
  }) => void;
}

export const WalletConnectContext = createContext<WalletConnectContextValue | null>(null);

function loadBindings(): Record<string, WcVaultBinding> {
  try {
    const raw = localStorage.getItem(BINDINGS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, WcVaultBinding>) : {};
  } catch {
    return {};
  }
}

function persistBindings(bindings: Record<string, WcVaultBinding>): void {
  try {
    localStorage.setItem(BINDINGS_STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // Quota / unavailable — non-fatal; sessions still work for this tab.
  }
}

/** Return a shallow copy of `obj` without `key` (avoids dynamic `delete`). */
function omitKey<T>(obj: Record<string, T>, key: string): Record<string, T> {
  const rest: Record<string, T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k !== key) rest[k] = v;
  }
  return rest;
}

/** A hex address param that differs from the vault address. */
function isOtherAccount(candidate: unknown, vaultAddress: string): boolean {
  return (
    typeof candidate === 'string' &&
    /^0x[0-9a-fA-F]{40}$/.test(candidate) &&
    candidate.toLowerCase() !== vaultAddress.toLowerCase()
  );
}

/** Normalise a hex-or-decimal string to a decimal wei string. */
function toWeiDecimal(value: string | undefined): string {
  if (!value) return '0';
  try {
    return BigInt(value).toString(); // BigInt parses both 0x… and decimal
  } catch {
    return '0';
  }
}

export function WalletConnectProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();

  const kitRef = useRef<IWalletKit | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeSessions, setActiveSessions] = useState<WcActiveSession[]>([]);
  const [bindings, setBindings] = useState<Record<string, WcVaultBinding>>(loadBindings);
  const [connecting, setConnecting] = useState(false);

  const [pendingProposal, setPendingProposal] = useState<WcSessionProposal | null>(null);
  const [txRequestSubmitting, setTxRequestSubmitting] = useState(false);

  // FIFO request queues (ENTX-A-4). The ref mirrors state so async handlers
  // always see the latest queue.
  const [txQueue, setTxQueue] = useState<PendingTxRequest[]>([]);
  const txQueueRef = useRef<PendingTxRequest[]>([]);
  const updateTxQueue = useCallback((next: PendingTxRequest[]) => {
    txQueueRef.current = next;
    setTxQueue(next);
  }, []);
  const [signQueue, setSignQueue] = useState<PendingSignRequest[]>([]);
  const signQueueRef = useRef<PendingSignRequest[]>([]);
  const updateSignQueue = useCallback((next: PendingSignRequest[]) => {
    signQueueRef.current = next;
    setSignQueue(next);
  }, []);
  const pendingTxRequest = txQueue[0] ?? null;
  const pendingSignRequest = signQueue[0] ?? null;

  const recordLabel = useCallback(
    (identity: DappIdentity) =>
      dappRecordLabel(identity, {
        unverified: t('walletconnect.verify.record_unverified'),
        mismatch: t('walletconnect.verify.record_mismatch'),
        unknownOrigin: t('walletconnect.verify.unknown_origin'),
      }),
    [t]
  );

  // The vault the in-flight pairing is being connected to, set just before pair().
  const pendingBindingRef = useRef<WcVaultBinding | null>(null);
  const [pendingProposalBinding, setPendingProposalBinding] = useState<WcVaultBinding | null>(null);

  // dApp transactions awaiting approval, for the optimistic-hash poll loop.
  const pendingTxsRef = useRef<Map<string, PendingDappTx>>(new Map());
  // dApp personal_sign requests awaiting the M-of-N signing threshold.
  const pendingSigsRef = useRef<Map<string, PendingDappSig>>(new Map());

  // Keep the bindings map in a ref so async event handlers read the latest value.
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;

  const updateBindings = useCallback((next: Record<string, WcVaultBinding>) => {
    setBindings(next);
    bindingsRef.current = next;
    persistBindings(next);
  }, []);

  const refreshSessions = useCallback(() => {
    const kit = kitRef.current;
    if (!kit) return;
    const sessions = kit.getActiveSessions();
    setActiveSessions(Object.values(sessions) as unknown as WcActiveSession[]);
  }, []);

  const respondToDapp = useCallback(
    async (topic: string, response: ReturnType<typeof formatJsonRpcResult>) => {
      const kit = kitRef.current;
      if (!kit) return;
      try {
        await kit.respondSessionRequest({ topic, response });
      } catch {
        // Request already expired / session gone — the proposal still lives in SSP.
      }
    },
    []
  );

  // ── Session request routing ────────────────────────────────────────────────
  const handleSessionRequest = useCallback(
    async (request: WcSessionRequest) => {
      const { topic, id } = request;
      const { method, params } = request.params.request;
      const binding = bindingsRef.current[topic];

      const reject = (msg: string) => respondToDapp(topic, formatJsonRpcError(id, msg) as never);

      if (!binding) {
        await reject(t('walletconnect.errors.no_binding'));
        return;
      }

      // Who is really asking: the Verify-attested origin of THIS request when
      // Verify attested it (or flagged it), else the one captured when the
      // session was approved — requests arrive over that session's encrypted
      // topic, so the approval-time attestation still identifies the peer.
      const kit = kitRef.current;
      const requestVerified = request.verifyContext?.verified;
      const verifyContext =
        requestVerified &&
        (requestVerified.isScam ||
          requestVerified.validation !== 'UNKNOWN' ||
          !binding.verifyContext)
          ? request.verifyContext
          : binding.verifyContext;
      const identity = resolveDappIdentity(
        verifyContext,
        kit?.getActiveSessions()[topic]?.peer.metadata ?? binding.dappMetadata
      );
      if (identity.verification === 'scam') {
        void message.error(
          t('walletconnect.errors.scam_dapp', {
            origin: identity.origin ?? t('walletconnect.verify.unknown_origin'),
          })
        );
        await reject(t('walletconnect.errors.request_refused'));
        return;
      }

      const chainId = chainKeyToChainId(binding.chain);
      // Requests are scoped to the vault's chain; anything else is refused.
      const requestOnVaultChain =
        !request.params.chainId || request.params.chainId === chainKeyToCaip2(binding.chain);

      switch (method) {
        case 'eth_accounts':
        case 'eth_requestAccounts':
          await respondToDapp(topic, formatJsonRpcResult(id, [binding.address]));
          return;
        case 'eth_chainId':
          await respondToDapp(
            topic,
            formatJsonRpcResult(id, chainId ? `0x${chainId.toString(16)}` : '0x0')
          );
          return;
        case 'net_version':
          await respondToDapp(topic, formatJsonRpcResult(id, String(chainId ?? 0)));
          return;
        case 'wallet_switchEthereumChain': {
          // Only the vault's own chain is switchable — the session is bound to it.
          const target = Array.isArray(params)
            ? parseInt((params[0] as { chainId?: string }).chainId ?? '0x0', 16)
            : 0;
          if (chainId && target === chainId) {
            await respondToDapp(topic, formatJsonRpcResult(id, null));
          } else {
            await reject(t('walletconnect.errors.chain_not_supported'));
          }
          return;
        }
        case 'wallet_addEthereumChain':
          await respondToDapp(topic, formatJsonRpcResult(id, null));
          return;
        case 'eth_sendTransaction': {
          const tx = Array.isArray(params)
            ? (params[0] as EthereumTransaction | undefined)
            : (params as EthereumTransaction | undefined);
          if (!tx || typeof tx !== 'object') {
            await reject(t('walletconnect.errors.invalid_transaction'));
            return;
          }
          if (!requestOnVaultChain) {
            await reject(t('walletconnect.errors.chain_not_supported'));
            return;
          }
          if (isOtherAccount(tx.from, binding.address)) {
            await reject(t('walletconnect.errors.account_mismatch'));
            return;
          }
          const call = classifyEvmCall({
            target: tx.to,
            value: tx.value ?? '0',
            data: tx.data,
            sender: binding.address,
          });
          if (!call) {
            await reject(t('walletconnect.errors.invalid_transaction'));
            return;
          }
          if (call.blocked) {
            // Self-calls / upgrades are never proposed (EVM call-safety rules).
            void message.error(t('walletconnect.errors.blocked_call'));
            await reject(t('walletconnect.errors.blocked_call'));
            return;
          }
          const queued = enqueueRequest(txQueueRef.current, {
            request,
            tx,
            binding,
            identity,
            call,
          });
          if (queued.accepted) updateTxQueue(queued.queue);
          else if (queued.reason === 'full')
            await reject(t('walletconnect.errors.too_many_requests'));
          return;
        }
        case 'personal_sign':
        case 'eth_sign': {
          // personal_sign params: [message, address]; eth_sign: [address, message]
          const arr = Array.isArray(params) ? (params as unknown[]) : [];
          const signMessage = method === 'personal_sign' ? arr[0] : arr[1];
          const signer = method === 'personal_sign' ? arr[1] : arr[0];
          if (typeof signMessage !== 'string') {
            await reject(t('walletconnect.errors.method_not_supported', { method }));
            return;
          }
          if (!requestOnVaultChain) {
            await reject(t('walletconnect.errors.chain_not_supported'));
            return;
          }
          if (isOtherAccount(signer, binding.address)) {
            await reject(t('walletconnect.errors.account_mismatch'));
            return;
          }
          const queued = enqueueRequest(signQueueRef.current, {
            request,
            message: signMessage,
            messageType: 'personal_sign' as const,
            binding,
            identity,
          });
          if (queued.accepted) updateSignQueue(queued.queue);
          else if (queued.reason === 'full')
            await reject(t('walletconnect.errors.too_many_requests'));
          return;
        }
        case 'eth_signTypedData_v4':
        case 'eth_signTypedData_v3':
        case 'eth_signTypedData': {
          // params: [address, typedDataJSON]. Keep the JSON verbatim — the backend
          // computes the canonical EIP-712 digest (domain + hashStruct).
          const arr = Array.isArray(params) ? (params as unknown[]) : [];
          const typed = arr[1];
          const json = typeof typed === 'string' ? typed : JSON.stringify(typed);
          if (!json || json === 'undefined') {
            await reject(t('walletconnect.errors.method_not_supported', { method }));
            return;
          }
          if (!requestOnVaultChain) {
            await reject(t('walletconnect.errors.chain_not_supported'));
            return;
          }
          if (isOtherAccount(arr[0], binding.address)) {
            await reject(t('walletconnect.errors.account_mismatch'));
            return;
          }
          const decoded = decodeTypedData(json);
          if (!decoded.ok) {
            await reject(t('walletconnect.errors.invalid_typed_data'));
            return;
          }
          // Typed data bound to another chain is refused outright.
          const typedDataChain = checkTypedDataChain(decoded.data, chainId);
          if (typedDataChain === 'mismatch') {
            void message.error(
              t('walletconnect.errors.typed_data_wrong_chain', {
                chainId: String(decoded.data.domain.chainId),
              })
            );
            await reject(
              t('walletconnect.errors.typed_data_wrong_chain', {
                chainId: String(decoded.data.domain.chainId),
              })
            );
            return;
          }
          const queued = enqueueRequest(signQueueRef.current, {
            request,
            message: json,
            messageType: 'eth_signTypedData_v4' as const,
            binding,
            identity,
            typedData: decoded.data,
            typedDataChain,
          });
          if (queued.accepted) updateSignQueue(queued.queue);
          else if (queued.reason === 'full')
            await reject(t('walletconnect.errors.too_many_requests'));
          return;
        }
        default:
          if (DEFERRED_SIGN_METHODS.has(method)) {
            await reject(t('walletconnect.errors.signing_not_supported'));
          } else {
            await reject(t('walletconnect.errors.method_not_supported', { method }));
          }
          return;
      }
    },
    [respondToDapp, t, message, updateTxQueue, updateSignQueue]
  );

  // Latest session_request handler, read via ref so the (once-attached) WalletKit
  // listener always calls the current closure.
  const handleSessionRequestRef = useRef(handleSessionRequest);
  handleSessionRequestRef.current = handleSessionRequest;

  // ── Lazy WalletKit initialisation ──────────────────────────────────────────
  // Loaded on demand: on first connect(), or on mount only if there are persisted
  // sessions to restore. Memoised so the heavy chunk loads (and inits) just once.
  const initPromiseRef = useRef<Promise<IWalletKit | null> | null>(null);
  const ensureKit = useCallback(async (): Promise<IWalletKit | null> => {
    if (kitRef.current) return kitRef.current;
    if (initPromiseRef.current) return initPromiseRef.current;
    initPromiseRef.current = (async () => {
      try {
        const [{ Core }, { WalletKit }] = await Promise.all([
          import('@walletconnect/core'),
          import('@reown/walletkit'),
        ]);
        const core = new Core({ projectId: WALLETCONNECT_PROJECT_ID });
        const kit = await WalletKit.init({
          core,
          metadata: {
            name: 'SSP Enterprise',
            description: 'Multi-party treasury coordination for SSP vaults.',
            url: 'https://sspwallet.io',
            icons: [
              'https://raw.githubusercontent.com/RunOnFlux/ssp-wallet/master/public/ssp-logo-black.svg',
            ],
          },
        });
        kitRef.current = kit;

        kit.on('session_proposal', (data) => {
          const proposal = data as unknown as WcSessionProposal;
          const identity = resolveDappIdentity(
            proposal.verifyContext,
            proposal.params.proposer.metadata
          );
          if (identity.verification === 'scam') {
            // Verify flags the origin as malicious: refuse before showing it.
            void message.error(
              t('walletconnect.errors.scam_dapp', {
                origin: identity.origin ?? t('walletconnect.verify.unknown_origin'),
              })
            );
            void import('@walletconnect/utils')
              .then(({ getSdkError }) =>
                kit.rejectSession({ id: proposal.id, reason: getSdkError('USER_REJECTED') })
              )
              .catch(() => undefined);
            pendingBindingRef.current = null;
            return;
          }
          // Bind to the vault the user chose right before pairing.
          setPendingProposalBinding(pendingBindingRef.current);
          setPendingProposal(proposal);
        });
        kit.on('session_request', (data) => {
          void handleSessionRequestRef.current(data as unknown as WcSessionRequest);
        });
        kit.on('session_delete', (event) => {
          const topic = (event as { topic: string }).topic;
          updateBindings(omitKey(bindingsRef.current, topic));
          updateTxQueue(removeTopic(txQueueRef.current, topic));
          updateSignQueue(removeTopic(signQueueRef.current, topic));
          refreshSessions();
        });
        kit.on('session_request_expire', (event) => {
          const id = (event as { id: number }).id;
          updateTxQueue(removeRequestId(txQueueRef.current, id));
          updateSignQueue(removeRequestId(signQueueRef.current, id));
        });

        // Sessions with no vault binding (e.g. left over from a previous user on
        // this browser — bindings are cleared at logout) are disconnected.
        // Storage is re-read right here: another tab may have paired (and
        // persisted its binding) while this tab's WalletKit was initialising,
        // and that live session must not be torn down as an orphan.
        const activeTopics = Object.keys(kit.getActiveSessions());
        const storedBindings = loadBindings();
        const adopted = adoptStoredBindings(activeTopics, bindingsRef.current, storedBindings);
        if (adopted) {
          // Already in storage: only bring this tab's copy up to date.
          bindingsRef.current = adopted;
          setBindings(adopted);
        }
        const orphanTopics = findOrphanTopics(activeTopics, bindingsRef.current, storedBindings);
        if (orphanTopics.length > 0) {
          const { getSdkError } = await import('@walletconnect/utils');
          await Promise.all(
            orphanTopics.map((topic) =>
              kit
                .disconnectSession({ topic, reason: getSdkError('USER_DISCONNECTED') })
                .catch(() => undefined)
            )
          );
        }

        setActiveSessions(Object.values(kit.getActiveSessions()) as unknown as WcActiveSession[]);
        setIsInitialized(true);
        return kit;
      } catch {
        // WalletConnect unavailable (offline / blocked) — the app works without it.
        initPromiseRef.current = null;
        return null;
      }
    })();
    return initPromiseRef.current;
  }, [refreshSessions, updateBindings, updateTxQueue, updateSignQueue, message, t]);

  // Restore persisted sessions on mount (only pays the bundle cost if the user has
  // active dApp connections). Users who never connect never load the engine.
  useEffect(() => {
    if (Object.keys(bindingsRef.current).length > 0) {
      void ensureKit();
    }
  }, [ensureKit]);

  // Keep this tab's bindings in step with other tabs (a pairing, a disconnect
  // or a logout there), so a later write here never persists a stale map that
  // drops another tab's binding.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== BINDINGS_STORAGE_KEY) return;
      const next = loadBindings();
      bindingsRef.current = next;
      setBindings(next);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ── Optimistic-hash poll loop ──────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const pending = pendingTxsRef.current;
      if (pending.size === 0) return;
      void (async () => {
        for (const [key, entry] of Array.from(pending.entries())) {
          try {
            const proposal = await vaultsApi.getProposal(
              entry.orgId,
              entry.vaultId,
              entry.proposalId
            );
            if (
              (proposal.status === 'broadcast' || proposal.status === 'confirmed') &&
              proposal.broadcastTxid
            ) {
              pending.delete(key);
              await respondToDapp(
                entry.topic,
                formatJsonRpcResult(entry.requestId, proposal.broadcastTxid)
              );
            } else if (
              proposal.status === 'rejected' ||
              proposal.status === 'cancelled' ||
              proposal.status === 'failed' ||
              proposal.status === 'expired' ||
              proposal.status === 'stale'
            ) {
              pending.delete(key);
              await respondToDapp(
                entry.topic,
                formatJsonRpcError(
                  entry.requestId,
                  t('walletconnect.errors.proposal_rejected')
                ) as never
              );
            } else if (Date.now() > entry.respondBy) {
              // Approval is taking longer than the dApp will wait — answer honestly.
              pending.delete(key);
              await respondToDapp(
                entry.topic,
                formatJsonRpcError(
                  entry.requestId,
                  t('walletconnect.errors.queued_for_approval')
                ) as never
              );
            }
          } catch {
            // Transient fetch error — retry on the next tick.
          }
        }
      })();
    }, PROPOSAL_POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [respondToDapp, t]);

  // ── Optimistic signature poll loop (personal_sign) ─────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const pending = pendingSigsRef.current;
      if (pending.size === 0) return;
      void (async () => {
        for (const [key, entry] of Array.from(pending.entries())) {
          try {
            const sigReq = await vaultsApi.getSignatureRequest(
              entry.orgId,
              entry.vaultId,
              entry.signatureRequestId
            );
            if (sigReq.status === 'signed' && sigReq.finalSignature) {
              pending.delete(key);
              await respondToDapp(
                entry.topic,
                formatJsonRpcResult(entry.requestId, sigReq.finalSignature)
              );
            } else if (sigReq.status === 'rejected' || sigReq.status === 'expired') {
              pending.delete(key);
              await respondToDapp(
                entry.topic,
                formatJsonRpcError(
                  entry.requestId,
                  t('walletconnect.errors.proposal_rejected')
                ) as never
              );
            } else if (Date.now() > entry.respondBy) {
              pending.delete(key);
              await respondToDapp(
                entry.topic,
                formatJsonRpcError(
                  entry.requestId,
                  t('walletconnect.errors.queued_for_approval')
                ) as never
              );
            }
          } catch {
            // Transient — retry next tick.
          }
        }
      })();
    }, PROPOSAL_POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [respondToDapp, t]);

  // ── Public actions ─────────────────────────────────────────────────────────
  const connect = useCallback(
    async (uri: string, binding: WcVaultBinding) => {
      setConnecting(true);
      pendingBindingRef.current = binding;
      try {
        const kit = await ensureKit();
        if (!kit) throw new Error(t('walletconnect.errors.not_initialized'));
        await kit.pair({ uri });
      } finally {
        setConnecting(false);
      }
    },
    [ensureKit, t]
  );

  const disconnect = useCallback(
    async (topic: string) => {
      const kit = kitRef.current;
      if (kit) {
        try {
          const { getSdkError } = await import('@walletconnect/utils');
          await kit.disconnectSession({ topic, reason: getSdkError('USER_DISCONNECTED') });
        } catch {
          // Already gone.
        }
      }
      updateBindings(omitKey(bindingsRef.current, topic));
      refreshSessions();
    },
    [refreshSessions, updateBindings]
  );

  const approveProposal = useCallback(async () => {
    const kit = kitRef.current;
    const proposal = pendingProposal;
    const binding = pendingProposalBinding;
    if (!kit || !proposal || !binding) return;

    const caip2 = chainKeyToCaip2(binding.chain);
    const account = toCaip10Account(binding.chain, binding.address);
    if (!caip2 || !account) {
      void message.error(t('walletconnect.errors.chain_not_supported'));
      setPendingProposal(null);
      return;
    }

    try {
      const { buildApprovedNamespaces } = await import('@walletconnect/utils');
      const approvedNamespaces = buildApprovedNamespaces({
        proposal: proposal.params as never,
        supportedNamespaces: {
          eip155: {
            chains: [caip2],
            methods: SUPPORTED_METHODS,
            events: SUPPORTED_EVENTS,
            accounts: [account],
          },
        },
      });
      const session = await kit.approveSession({ id: proposal.id, namespaces: approvedNamespaces });
      const meta = proposal.params.proposer.metadata;
      const boundWithIdentity: WcVaultBinding = {
        ...binding,
        ...(proposal.verifyContext ? { verifyContext: proposal.verifyContext } : {}),
        dappMetadata: { name: meta.name, url: meta.url },
      };
      const next = { ...bindingsRef.current, [session.topic]: boundWithIdentity };
      updateBindings(next);
      refreshSessions();
      const identity = resolveDappIdentity(proposal.verifyContext, meta);
      void message.success(t('walletconnect.connected', { dapp: recordLabel(identity) }));
    } catch {
      // Usually: the dApp requires a chain this vault can't provide.
      void message.error(t('walletconnect.errors.approve_failed'));
      try {
        const { getSdkError } = await import('@walletconnect/utils');
        await kit.rejectSession({
          id: proposal.id,
          reason: getSdkError('UNSUPPORTED_CHAINS'),
        });
      } catch {
        // ignore
      }
    } finally {
      setPendingProposal(null);
      setPendingProposalBinding(null);
      pendingBindingRef.current = null;
    }
  }, [
    pendingProposal,
    pendingProposalBinding,
    message,
    t,
    updateBindings,
    refreshSessions,
    recordLabel,
  ]);

  const rejectProposal = useCallback(async () => {
    const kit = kitRef.current;
    const proposal = pendingProposal;
    if (kit && proposal) {
      try {
        const { getSdkError } = await import('@walletconnect/utils');
        await kit.rejectSession({ id: proposal.id, reason: getSdkError('USER_REJECTED') });
      } catch {
        // ignore
      }
    }
    setPendingProposal(null);
    setPendingProposalBinding(null);
    pendingBindingRef.current = null;
  }, [pendingProposal]);

  const confirmTxRequest = useCallback(async () => {
    const req = pendingTxRequest;
    if (!req) return;
    setTxRequestSubmitting(true);
    try {
      const { tx, binding, request } = req;
      const data = tx.data && tx.data !== '0x' ? tx.data : undefined;
      const proposal = await vaultsApi.createProposal(binding.orgId, binding.vaultId, {
        recipients: [{ address: tx.to ?? '', amount: toWeiDecimal(tx.value) }],
        customData: data,
        sourceAddressIndex: binding.addressIndex,
        // Origin-based label (Verify API), never the dApp's self-declared name:
        // co-signers and devices see this memo.
        memo: t('walletconnect.proposal_memo', { dapp: recordLabel(req.identity) }),
      });
      // Track for optimistic-hash resolution; keep the dApp request open for now.
      pendingTxsRef.current.set(`${request.topic}:${String(request.id)}`, {
        topic: request.topic,
        requestId: request.id,
        proposalId: proposal.id,
        orgId: binding.orgId,
        vaultId: binding.vaultId,
        respondBy: Date.now() + OPTIMISTIC_WINDOW_MS,
      });
      updateTxQueue(removeRequest(txQueueRef.current, request.topic, request.id));
      void message.success(t('walletconnect.proposal_created'));
      void navigate(`/vaults/${binding.vaultId}/proposals/${proposal.id}?org=${binding.orgId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('walletconnect.errors.proposal_failed');
      void message.error(msg);
      // Tell the dApp the request failed so it doesn't hang.
      await respondToDapp(req.request.topic, formatJsonRpcError(req.request.id, msg) as never);
      updateTxQueue(removeRequest(txQueueRef.current, req.request.topic, req.request.id));
    } finally {
      setTxRequestSubmitting(false);
    }
  }, [pendingTxRequest, message, navigate, respondToDapp, t, recordLabel, updateTxQueue]);

  const rejectTxRequest = useCallback(async () => {
    const req = pendingTxRequest;
    if (!req) return;
    const { getSdkError } = await import('@walletconnect/utils');
    await respondToDapp(
      req.request.topic,
      formatJsonRpcError(req.request.id, getSdkError('USER_REJECTED').message) as never
    );
    updateTxQueue(removeRequest(txQueueRef.current, req.request.topic, req.request.id));
  }, [pendingTxRequest, respondToDapp, updateTxQueue]);

  const rejectSignRequest = useCallback(async () => {
    const req = pendingSignRequest;
    if (!req) return;
    const { getSdkError } = await import('@walletconnect/utils');
    await respondToDapp(
      req.request.topic,
      formatJsonRpcError(req.request.id, getSdkError('USER_REJECTED').message) as never
    );
    updateSignQueue(removeRequest(signQueueRef.current, req.request.topic, req.request.id));
  }, [pendingSignRequest, respondToDapp, updateSignQueue]);

  // Drop the head sign request once it was handed to the signature-request flow.
  const clearSignRequest = useCallback(() => {
    const head = signQueueRef.current[0];
    if (!head) return;
    updateSignQueue(removeRequest(signQueueRef.current, head.request.topic, head.request.id));
  }, [updateSignQueue]);

  // Hold the dApp request open; the signature poll loop resolves it once the
  // M-of-N signature request completes.
  const registerPendingSignature = useCallback(
    (entry: {
      topic: string;
      requestId: number;
      signatureRequestId: string;
      orgId: string;
      vaultId: string;
    }) => {
      pendingSigsRef.current.set(`${entry.topic}:${String(entry.requestId)}`, {
        ...entry,
        respondBy: Date.now() + OPTIMISTIC_WINDOW_MS,
      });
    },
    []
  );

  const value = useMemo<WalletConnectContextValue>(
    () => ({
      isInitialized,
      activeSessions,
      bindings,
      connect,
      disconnect,
      connecting,
      pendingProposal,
      pendingProposalBinding,
      approveProposal,
      rejectProposal,
      pendingTxRequest,
      txQueueSize: txQueue.length,
      confirmTxRequest,
      rejectTxRequest,
      txRequestSubmitting,
      pendingSignRequest,
      signQueueSize: signQueue.length,
      recordLabel,
      rejectSignRequest,
      clearSignRequest,
      registerPendingSignature,
    }),
    [
      isInitialized,
      activeSessions,
      bindings,
      connect,
      disconnect,
      connecting,
      pendingProposal,
      pendingProposalBinding,
      approveProposal,
      rejectProposal,
      pendingTxRequest,
      txQueue.length,
      confirmTxRequest,
      rejectTxRequest,
      txRequestSubmitting,
      pendingSignRequest,
      signQueue.length,
      recordLabel,
      rejectSignRequest,
      clearSignRequest,
      registerPendingSignature,
    ]
  );

  return <WalletConnectContext.Provider value={value}>{children}</WalletConnectContext.Provider>;
}
